import i18n from '@/utils/i18n';
import { type FC, useCallback, useState } from 'react';
import { SearchInput } from '@usepepper/sprout-kit-ui';
import { useDebouncedCallback } from 'use-debounce';

const SEARCH_DEBOUNCE_DELAY_MS = 500;

const MIN_SEARCH_TERM_LENGTH = 2;

interface Props {
  appliedSearchTerm: string;
  onSubmit: (search: string) => void;
}

export const OrderInboundsOrderListFilterFormSearchInput: FC<Props> = ({
  appliedSearchTerm,
  onSubmit,
}) => {
  const [searchText, setSearchText] = useState(appliedSearchTerm);

  const debouncedSearchSubmit = useDebouncedCallback((text: string) => {
    // Anything shorter than the minimum is treated as no search at all, so
    // backspacing down to a single character clears the term instead of
    // firing a near-unfiltered query.
    const normalizedText = text.trim().toLowerCase();
    onSubmit(
      normalizedText.length >= MIN_SEARCH_TERM_LENGTH ? normalizedText : '',
    );
  }, SEARCH_DEBOUNCE_DELAY_MS);

  const handleChangeText = useCallback(
    (text: string) => {
      debouncedSearchSubmit.cancel();
      setSearchText(text);
      debouncedSearchSubmit(text);
    },
    [debouncedSearchSubmit],
  );

  const handleClear = useCallback(() => {
    debouncedSearchSubmit.cancel();
    setSearchText('');
    debouncedSearchSubmit('');
  }, [debouncedSearchSubmit]);

  return (
    <SearchInput
      onChangeText={handleChangeText}
      onClear={handleClear}
      placeholder={i18n.t('search')}
      value={searchText}
    />
  );
};
